import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProductListItem = ({ product, onAddToCart, onToggleWishlist }) => {
  const [isWishlisted, setIsWishlisted] = useState(product?.isWishlisted || false);
  const [isLoading, setIsLoading] = useState(false);

  const handleAddToCart = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      if (onAddToCart) {
        onAddToCart(product);
      }
    } catch (error) {
      console.error('Failed to add to cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleWishlist = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    const newWishlistState = !isWishlisted;
    setIsWishlisted(newWishlistState);
    
    if (onToggleWishlist) {
      onToggleWishlist(product?.id, newWishlistState);
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars?.push(
        <Icon key={i} name="Star" size={14} className="text-warning fill-current" />
      );
    }
    
    if (hasHalfStar) {
      stars?.push(
        <Icon key="half" name="Star" size={14} className="text-warning fill-current opacity-50" />
      );
    }
    
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars?.push(
        <Icon key={`empty-${i}`} name="Star" size={14} className="text-muted-foreground" />
      );
    }
    
    return stars;
  };

  return (
    <Link 
      to={`/product-details?id=${product?.id}`}
      className="group flex gap-4 p-4 bg-card rounded-xl border border-border hover:shadow-elevation-2 transition-all duration-300"
    >
      {/* Image */}
      <div className="relative flex-shrink-0 w-24 h-24 sm:w-32 sm:h-32 overflow-hidden bg-muted rounded-lg">
        <Image
          src={product?.image}
          alt={product?.imageAlt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        
        {/* Badges */}
        <div className="absolute top-1 left-1 flex flex-col gap-1">
          {product?.isNew && (
            <span className="px-1.5 py-0.5 bg-success text-success-foreground text-xs font-medium rounded">
              New
            </span>
          )}
          {product?.discount && (
            <span className="px-1.5 py-0.5 bg-error text-error-foreground text-xs font-medium rounded">
              -{product?.discount}%
            </span>
          )}
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            {/* Brand */}
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">
              {product?.brand}
            </p>
            
            {/* Product Name */}
            <h3 className="text-base font-medium text-foreground line-clamp-2 group-hover:text-primary transition-colors">
              {product?.name}
            </h3>
          </div>

          {/* Wishlist Button */}
          <button
            onClick={handleToggleWishlist}
            className="flex-shrink-0 w-8 h-8 bg-muted rounded-full flex items-center justify-center hover:bg-muted/80 transition-colors ml-2"
          >
            <Icon 
              name="Heart" 
              size={16} 
              className={isWishlisted ? "text-error fill-current" : "text-muted-foreground"} 
            />
          </button>
        </div>

        {/* Rating & Reviews */}
        <div className="flex items-center gap-2 mb-2">
          <div className="flex items-center gap-1">
            {renderStars(product?.rating)}
          </div>
          <span className="text-sm text-muted-foreground">
            {product?.rating} ({product?.reviewCount} reviews)
          </span>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
          {product?.description}
        </p>

        {/* Price and Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-foreground">
              ${product?.price?.toFixed(2)}
            </span>
            {product?.originalPrice && product?.originalPrice > product?.price && (
              <span className="text-sm text-muted-foreground line-through">
                ${product?.originalPrice?.toFixed(2)}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Stock Status */}
            <div className="flex items-center gap-1">
              <div className={`w-2 h-2 rounded-full ${
                product?.stock > 10 ? 'bg-success' : 
                product?.stock > 0 ? 'bg-warning' : 'bg-error'
              }`} />
              <span className="text-xs text-muted-foreground">
                {product?.stock > 10 ? 'In Stock' : 
                 product?.stock > 0 ? `${product?.stock} left` : 'Out of Stock'}
              </span>
            </div>

            {/* Add to Cart Button */}
            <Button
              variant="outline"
              size="sm"
              loading={isLoading}
              onClick={handleAddToCart}
              disabled={product?.stock === 0}
              iconName="ShoppingBag"
              iconPosition="left"
            >
              Add to Cart
            </Button>
          </div>
        </div>

        {/* Variants Preview */}
        {product?.variants && product?.variants?.length > 0 && (
          <div className="flex items-center gap-2 mt-3">
            <span className="text-xs text-muted-foreground">Available in:</span>
            <div className="flex gap-1">
              {product?.variants?.slice(0, 6)?.map((variant, index) => (
                <div
                  key={index}
                  className="w-4 h-4 rounded-full border border-border"
                  style={{ backgroundColor: variant?.color }}
                  title={variant?.name}
                />
              ))}
              {product?.variants?.length > 6 && (
                <span className="text-xs text-muted-foreground ml-1">
                  +{product?.variants?.length - 6} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </Link>
  );
};

export default ProductListItem;